import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import {
	Checkbox,
	Chip,
	FormControlLabel,
	Grid2,
	IconButton,
	TextField,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { get } from "lodash-es";
import { useMemo, useState } from "react";
import type { EmailRecipient } from "@setup/configurations/email-recipients/models/EmailRecipient";
import { getEmailRecipients } from "@setup/configurations/email-recipients/repositories/EmailRecipientsRepository";
import { RecipientsPicker } from "./recipients-picker";

type Props = {
	checked: boolean;
	onCheckedChange: (checked: boolean) => void;
	recipientIds: number[];
	onRecipientIdsChange: (ids: number[]) => void;
	templateId: number | null;
	/**
	 * The modal's own `root` and `inputEditable` classes. `root` is what draws the floating label
	 * over the outline the way every other field in this form does, so without it the label sits
	 * wrong; `inputEditable` is the thicker blue border.
	 */
	fieldClassName?: string;
};

const MAX_CHIPS = 3;

/**
 * One row of the form: the switch, and next to it who the email goes to.
 *
 * Built to read as another outlined field rather than as a loose control, the same way the Asset
 * field is a read-only box with a picker behind an icon. The names sit inside it as chips so the
 * selection is visible without opening anything, which is the whole point of putting it here
 * instead of hiding it behind a button.
 *
 * Unticking hides nothing and deletes nobody: the people stay on the template, so switching
 * notification off for a while and back on does not mean picking everyone again.
 */
export const NotifyRecipientsField = ({
	checked,
	onCheckedChange,
	recipientIds,
	onRecipientIdsChange,
	templateId,
	fieldClassName,
}: Props) => {
	const [pickerOpen, setPickerOpen] = useState(false);

	const { data } = useQuery({
		queryKey: ["emailRecipients"],
		queryFn: getEmailRecipients,
	});

	const names = useMemo(() => {
		const book = get(data, "data.tables[0].rows", []) as EmailRecipient[];
		return recipientIds
			.map(
				(id) => book.find((r) => r.RecipientId === id)?.DisplayName ?? `#${id}`,
			)
			.sort((a, b) => a.localeCompare(b));
	}, [data, recipientIds]);

	const openPicker = () => {
		if (checked) setPickerOpen(true);
	};

	const chips = names
		.slice(0, MAX_CHIPS)
		.map((n) => <Chip key={n} label={n} size="small" sx={{ mr: 0.5 }} />);
	if (names.length > MAX_CHIPS) {
		chips.push(
			<Chip
				key="more"
				label={`+${names.length - MAX_CHIPS}`}
				size="small"
				variant="outlined"
				sx={{ mr: 0.5 }}
			/>,
		);
	}

	return (
		<>
			<Grid2 container alignItems="center" size={{ md: 12, xs: 12 }}>
				<Grid2 style={{ padding: "0 10px 0 0" }} size={{ md: "auto", xs: 12 }}>
					<FormControlLabel
						control={
							<Checkbox
								checked={checked}
								onChange={(e) => onCheckedChange(e.target.checked)}
							/>
						}
						label="Email when work orders are generated"
						slotProps={{
							typography: { fontSize: 14, whiteSpace: "nowrap" },
						}}
					/>
				</Grid2>
				<Grid2 style={{ padding: "0 10px 0 0" }} size={{ md: "grow", xs: 12 }}>
					<TextField
						className={checked ? fieldClassName : undefined}
						label="Recipients"
						variant="outlined"
						fullWidth
						margin="dense"
						disabled={!checked}
						onClick={openPicker}
						placeholder={checked && !names.length ? "Nobody selected" : ""}
						value=""
						InputLabelProps={{ shrink: true, style: { fontSize: 14 } }}
						InputProps={{
							readOnly: true,
							style: { cursor: checked ? "pointer" : "default" },
							startAdornment: chips.length ? (
								<div style={{ display: "flex", flexWrap: "nowrap" }}>
									{chips}
								</div>
							) : null,
							endAdornment: (
								<IconButton
									size="small"
									disabled={!checked}
									onClick={(e) => {
										e.stopPropagation();
										openPicker();
									}}
									aria-label="select recipients"
								>
									<PeopleAltOutlinedIcon />
								</IconButton>
							),
						}}
					/>
				</Grid2>
			</Grid2>

			<RecipientsPicker
				show={pickerOpen}
				templateId={templateId}
				value={recipientIds}
				onHide={(ids) => {
					if (ids) onRecipientIdsChange(ids);
					setPickerOpen(false);
				}}
			/>
		</>
	);
};
